"use client";

import { LocaleLink } from "@i18n/routing";
import { CircularScore } from "@marketing/ai-credit-report/ui/CircularScore";
import { RotateCcwIcon } from "lucide-react";
import type { AipvsScoreResponse } from "../lib/api";

interface AipvsScoreCardProps {
	score: AipvsScoreResponse;
	/** Client pages reset in place. */
	onReset?: () => void;
	/**
	 * Per-domain pages have no in-place result to clear, so they send the
	 * visitor back to the tool instead. Takes precedence over `onReset`.
	 */
	resetHref?: string;
}

const tierColors: Record<number, string> = {
	1: "#059669",
	2: "#2563eb",
	3: "#d97706",
	4: "#dc2626",
};

export function AipvsScoreCard({
	score,
	onReset,
	resetHref,
}: AipvsScoreCardProps) {
	return (
		<div className="bg-card rounded-xl border p-6">
			<div className="flex items-center gap-5">
				<CircularScore
					score={Math.round(score.aipvs)}
					size="md"
					brandColor={tierColors[score.tier]}
					showVariability={false}
				/>
				<div className="flex-1 min-w-0">
					<h3 className="text-lg font-semibold mb-1">
						AI Placement Value Score
					</h3>
					<div className="flex flex-wrap items-center gap-3 mb-2">
						<span
							className="text-sm font-medium px-2.5 py-0.5 rounded-full"
							style={{
								color: tierColors[score.tier],
								border: `1px solid ${tierColors[score.tier]}`,
							}}
						>
							Tier {score.tier}: {score.tierLabel}
						</span>
						<span className="text-sm text-muted-foreground">
							Organic Authority:{" "}
							<span className="font-medium text-foreground">
								{score.layers.oab.score.toFixed(1)}
							</span>
						</span>
						<span className="text-sm text-muted-foreground">
							AI Impact Multiplier:{" "}
							<span className="font-semibold text-foreground">
								{score.aiImpactMultiplier.toFixed(3)}
							</span>
						</span>
					</div>
					<div className="flex items-center gap-3">
						<LocaleLink
							href="/ai-placement-value-score"
							className="text-sm text-primary hover:underline"
						>
							What&apos;s this?
						</LocaleLink>
						<span className="text-muted-foreground">|</span>
						<LocaleLink
							href="/docs/methodology/ai-placement-value-score"
							className="text-sm text-muted-foreground hover:text-foreground underline"
						>
							How is this calculated?
						</LocaleLink>
					</div>
				</div>
				{resetHref ? (
					<LocaleLink
						href={resetHref}
						className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 shrink-0"
					>
						<RotateCcwIcon className="w-4 h-4" />
						Check another domain
					</LocaleLink>
				) : onReset ? (
					<button
						type="button"
						onClick={onReset}
						className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 shrink-0"
					>
						<RotateCcwIcon className="w-4 h-4" />
						Check another
					</button>
				) : null}
			</div>
		</div>
	);
}
